<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb :title="page.title" :breadcrumbs="breadcrumbs"></BaseBreadcrumb>

         <v-card>
            <v-toolbar dark color="primary">
               <v-icon left>mdi-account-arrow-up</v-icon>
               <v-toolbar-title>Corporate Upgrade Information</v-toolbar-title>
               <v-spacer></v-spacer>
               <v-chip small :color="statusColor" dark>{{ statusLabel }}</v-chip>
            </v-toolbar>

            <v-card-text>
               <v-alert v-if="flash.status" type="success" dense text>{{ flash.status }}</v-alert>
               <v-alert v-if="flash.error" type="error" dense text>{{ flash.error }}</v-alert>

               <v-card outlined class="mb-5 applicant">
                  <v-card-text>
                     <v-row align="center">
                        <v-col cols="12" sm="auto">
                           <v-avatar size="76" color="blue lighten-5">
                              <v-img :src="profilePhotoSrc" :alt="memberinfo.fullname"></v-img>
                           </v-avatar>
                        </v-col>
                        <v-col cols="12" md="5">
                           <div class="overline">Applicant</div>
                           <div class="text-h6 font-weight-bold">{{ memberinfo.fullname }}</div>
                           <div>{{ memberinfo.contactemail }}</div>
                        </v-col>
                        <v-col cols="6" md="3">
                           <div class="caption">Member ID</div>
                           <strong>{{ memberinfo.memberid || '-' }}</strong>
                        </v-col>
                        <v-col cols="6" md="2">
                           <div class="caption">Previous Type</div>
                           <strong>{{ memberinfo.applcorp_previous_membertype || 'GRA' }}</strong>
                        </v-col>
                     </v-row>
                  </v-card-text>
               </v-card>

               <v-alert v-if="status===3" type="warning" dense text>
                  <strong>Application Rejected.</strong> Admin can reopen this application to allow the member to resubmit.
               </v-alert>

               <v-row v-if="[1,2,4,5].includes(status)" dense class="mb-4">
                  <v-col cols="12" md="6">
                     <v-card outlined>
                        <v-card-text class="py-3">
                           <div class="caption grey--text mb-1">Proposer</div>
                           <div class="body-2 font-weight-medium">{{ proposer ? proposer.fullname : 'Not assigned' }}</div>
                           <div v-if="proposer" class="caption grey--text">{{ proposer.memberid || '-' }} ({{ proposer.membertype }})</div>
                           <v-chip small dark class="mt-2" :color="endorsementColor(memberinfo.applcorp_proposer_status)">
                              {{ endorsementLabel(memberinfo.applcorp_proposer_status) }}
                           </v-chip>
                           <div v-if="memberinfo.applcorp_proposer_responded_at" class="caption mt-1">
                              Responded: {{ formatDate(memberinfo.applcorp_proposer_responded_at) }}
                           </div>
                        </v-card-text>
                     </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                     <v-card outlined>
                        <v-card-text class="py-3">
                           <div class="caption grey--text mb-1">Seconder</div>
                           <div class="body-2 font-weight-medium">{{ seconder ? seconder.fullname : 'Not assigned' }}</div>
                           <div v-if="seconder" class="caption grey--text">{{ seconder.memberid || '-' }} ({{ seconder.membertype }})</div>
                           <v-chip small dark class="mt-2" :color="endorsementColor(memberinfo.applcorp_seconder_status)">
                              {{ endorsementLabel(memberinfo.applcorp_seconder_status) }}
                           </v-chip>
                           <div v-if="memberinfo.applcorp_seconder_responded_at" class="caption mt-1">
                              Responded: {{ formatDate(memberinfo.applcorp_seconder_responded_at) }}
                           </div>
                        </v-card-text>
                     </v-card>
                  </v-col>
               </v-row>

               <table-corp-exp ref="corpexp" class="mb-5" :readonly="true"></table-corp-exp>

               <v-card outlined class="mb-5">
                  <v-card-title class="section-title">
                     <div>
                        <div>Corporate Upgrade Milestones</div>
                        <div class="section-subtitle">Admin controlled dates, result remarks, and supporting documents.</div>
                     </div>
                  </v-card-title>
                  <v-card-text>
                     <milestone-group title="Course" :items="courseMilestones" :files="milestoneFiles" :status-options="statusOptions" @view="openDocument"></milestone-group>
                     <milestone-group title="Interview" :items="interviewMilestones" :files="milestoneFiles" :status-options="statusOptions" @view="openDocument"></milestone-group>
                     <milestone-group title="Exam" :items="examMilestones" :files="milestoneFiles" :status-options="statusOptions" @view="openDocument"></milestone-group>
                  </v-card-text>
               </v-card>

               <v-form>
                  <v-textarea v-model="form.notes" label="Admin Notes" outlined rows="3"></v-textarea>
                  <v-file-input v-model="form.document" label="Replace Main Support Document" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" outlined dense show-size></v-file-input>
                  <v-btn v-if="mainDocumentUrl" text small color="primary" :href="mainDocumentUrl" target="_blank">
                     <v-icon left small>mdi-file-document-outline</v-icon>View current document
                  </v-btn>
               </v-form>
            </v-card-text>

            <v-card-actions>
               <v-btn text href="/admin/corporate-upgrades">Back</v-btn>
               <v-spacer></v-spacer>
               <v-btn color="primary" :loading="saving" @click="save">Save Upgrade Info</v-btn>
               <v-btn v-if="status===3" color="orange darken-2" dark :loading="reopening" @click="reopen">
                  <v-icon left>mdi-restore</v-icon>Reopen Application
               </v-btn>
               <v-btn v-if="status===1&&bothEndorsed" color="success" dark @click="approve">Approve</v-btn>
               <v-btn v-if="status===1&&!bothEndorsed" color="success" dark disabled title="Waiting for proposer and seconder approval">Approve</v-btn>
               <v-btn v-if="[1,2,4].includes(status)" color="error" dark @click="reject">{{ status===2 ? 'Management Reject' : 'Reject' }}</v-btn>
            </v-card-actions>
         </v-card>
      </v-container>
   </dashboard-layout>
</template>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import TableCorpExp from './Tables/TableCorpExp.vue'

const MilestoneGroup = {
   props: ['title', 'items', 'files', 'statusOptions'],
   methods: {
      documentUrl(item) {
         return item.document && item.documentpath ? `/${item.documentpath}/${item.document}` : ''
      },
   },
   template: `<div class="mb-5">
      <div class="milestone-heading">{{ title }}</div>
      <v-simple-table dense class="milestone-table">
         <template v-slot:default>
            <thead>
               <tr>
                  <th style="width:180px">Item</th>
                  <th style="width:170px">Date</th>
                  <th style="width:170px">Result</th>
                  <th>Remarks</th>
                  <th style="width:260px">Document</th>
               </tr>
            </thead>
            <tbody>
               <tr v-for="item in items" :key="item.milestone_key">
                  <td class="font-weight-medium">{{ item.label }}</td>
                  <td><v-text-field v-model="item.milestone_date" type="date" dense outlined hide-details></v-text-field></td>
                  <td><v-select v-model="item.result_status" :items="statusOptions" dense outlined hide-details clearable></v-select></td>
                  <td><v-textarea v-model="item.remarks" rows="1" auto-grow dense outlined hide-details></v-textarea></td>
                  <td>
                     <div class="d-flex align-center">
                        <v-file-input v-model="files[item.milestone_key]" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" dense outlined hide-details show-size class="mr-2"></v-file-input>
                        <v-btn icon color="primary" :disabled="!documentUrl(item)" @click="$emit('view', documentUrl(item))">
                           <v-icon>mdi-eye</v-icon>
                        </v-btn>
                     </div>
                  </td>
               </tr>
            </tbody>
         </template>
      </v-simple-table>
   </div>`,
}

export default {
   name: 'MOACorporateUpgradeInfo',
   components: { DashboardLayout, BaseBreadcrumb, TableCorpExp, MilestoneGroup },
   props: {
      memberinfo: { type: Object, required: true },
      proposer: { type: Object, default: null },
      seconder: { type: Object, default: null },
      corporateMilestones: { type: Array, default: () => [] },
   },
   data() {
      const files = {}
      const milestones = this.corporateMilestones.map(item => {
         files[item.milestone_key] = null
         return Object.assign({}, item)
      })

      return {
         page: { title: 'Corporate Upgrade Information' },
         breadcrumbs: [
            { text: 'Dashboard', disabled: false, to: '/dashboard' },
            { text: 'Corporate Applications', disabled: false, to: '/admin/corporate-upgrades' },
            { text: 'Information', disabled: true },
         ],
         saving: false,
         reopening: false,
         milestoneFiles: files,
         statusOptions: [
            { text: 'Pending', value: 'pending' },
            { text: 'Passed', value: 'passed' },
            { text: 'Failed', value: 'failed' },
            { text: 'Not Required', value: 'not_required' },
         ],
         form: {
            notes: this.memberinfo.applcorp_notes || '',
            document: null,
            milestones,
         },
      }
   },
   computed: {
      status() { return parseInt(this.memberinfo.applcorpstatus || 0) },
      statusLabel() { return ({ 1: 'Pending Review', 2: 'Approved', 3: 'Rejected', 4: 'Need Resubmission', 5: 'Pending Endorsement / Payment' })[this.status] || 'Not Submitted' },
      statusColor() { return ({ 1: 'orange darken-2', 2: 'green', 3: 'red', 4: 'deep-purple', 5: 'blue darken-2' })[this.status] || 'grey' },
      flash() { return (this.$page && (this.$page.props || this.$page).flash) || {} },
      profilePhotoSrc() { return this.memberinfo.userid ? `/member/photo/${this.memberinfo.userid}?v=${Date.now()}` : '' },
      mainDocumentUrl() { return this.memberinfo.applcorp_document && this.memberinfo.applcorp_documentpath ? `/${this.memberinfo.applcorp_documentpath}/${this.memberinfo.applcorp_document}` : null },
      bothEndorsed() { return parseInt(this.memberinfo.applcorp_proposer_status || 0) === 1 && parseInt(this.memberinfo.applcorp_seconder_status || 0) === 1 },
      courseMilestones() { return this.form.milestones.filter(item => item.milestone_type === 'course') },
      interviewMilestones() { return this.form.milestones.filter(item => item.milestone_type === 'interview') },
      examMilestones() { return this.form.milestones.filter(item => item.milestone_type === 'exam') },
   },
   methods: {
      endorsementLabel(value) { return ({ 0: 'Pending', 1: 'Approved', 2: 'Rejected' })[parseInt(value || 0)] || 'Pending' },
      endorsementColor(value) { return ({ 0: 'orange darken-2', 1: 'green', 2: 'red' })[parseInt(value || 0)] || 'orange darken-2' },
      formatDate(value) { if (!value) return '-'; return new Date(value).toLocaleDateString('en-MY', { day: '2-digit', month: '2-digit', year: 'numeric' }) },
      openDocument(url) { if (url) window.open(url) },
      save() {
         const data = new FormData()
         data.append('userid', this.memberinfo.userid)
         data.append('notes', this.form.notes || '')
         data.append('milestones', JSON.stringify(this.form.milestones.map(item => ({
            milestone_key: item.milestone_key,
            milestone_date: item.milestone_date || '',
            result_status: item.result_status || '',
            remarks: item.remarks || '',
         }))))

         if (this.form.document) {
            data.append('document', this.form.document)
         }

         Object.keys(this.milestoneFiles).forEach(key => {
            if (this.milestoneFiles[key]) {
               data.append(`milestone_documents[${key}]`, this.milestoneFiles[key])
            }
         })

         this.saving = true
         this.$inertia.post('/admin/member/upgrade-corporate/update-info', data, {
            preserveScroll: true,
            onFinish: () => { this.saving = false },
         })
      },
      approve() { if (confirm('Approve this Corporate Upgrade application?')) this.$inertia.post('/admin/member/upgrade-corporate/approve', { userid: this.memberinfo.userid }) },
      reject() { if (confirm('Reject this Corporate Upgrade application?')) this.$inertia.post('/admin/member/upgrade-corporate/reject', { userid: this.memberinfo.userid }) },
      reopen() {
         if (confirm('Reopen this Corporate Upgrade application? Member will be able to resubmit.')) {
            this.reopening = true
            this.$inertia.post('/admin/member/upgrade-corporate/reopen', { userid: this.memberinfo.userid }, { onFinish: () => { this.reopening = false } })
         }
      },
   },
   mounted() {
      if (this.$refs.corpexp) {
         this.$refs.corpexp.setuserid(this.memberinfo.userid)
      }
   },
}
</script>

<style scoped>
.applicant {
   border-left: 4px solid #1976d2 !important;
}

.section-title {
   align-items: center;
   border-bottom: 1px solid #e5e7eb;
}

.section-subtitle {
   color: #64748b;
   font-size: 12px;
   font-weight: 400;
   margin-top: 2px;
}

.milestone-heading {
   color: #0f172a;
   font-size: 14px;
   font-weight: 700;
   margin-bottom: 8px;
   text-transform: uppercase;
}

.milestone-table {
   border: 1px solid #e5e7eb;
   border-radius: 6px;
}
</style>
